import { useState } from 'react';
import { Box, Button, Flex, Icon, SimpleGrid, Text, VStack } from '@chakra-ui/react';
import { LuArrowRight, LuCheck, LuChevronDown, LuChevronUp, LuTriangleAlert, LuX } from 'react-icons/lu';
import { useTranslation } from 'react-i18next';
import { AMOUNT_COLOR, Tag, directedMoney, movementLabel } from './CheckupParts';
import { cardSurface } from '../../shared/Card';

// The facts the check carries of a movement, by id.
const factsOf = (item, id) => (item.movements || []).find((movement) => movement.id === id) || null;

// "One" or "Other": the plural form of a key for `count`.
const plural = (count) => (count === 1 ? 'One' : 'Other');

function Tile({ label, value, sub, good }) {
  return (
    <Box bg="redesign.bg.card" borderRadius="10px" px={4} py={3}>
      <Text textStyle="microLabel">{label}</Text>
      <Flex align="center" gap={2} mt={1} fontSize="lg" fontWeight={600} color="redesign.heading" sx={{ fontVariantNumeric: 'tabular-nums' }}>
        {value}
        {good != null && (
          <>
            <Icon as={LuArrowRight} boxSize="16px" color="redesign.text.muted" />
            <Text as="span" color="redesign.amount.income">{good}</Text>
          </>
        )}
      </Flex>
      <Text fontSize="xs" color="redesign.text.muted" mt={1}>{sub}</Text>
    </Box>
  );
}

// One reading of the bank row: today's, which stays, or the earlier one, which goes.
function Reading({ side, currency, stays }) {
  const { t } = useTranslation('repairs');
  return (
    <Flex align="center" gap={3} wrap="wrap" sx={{ fontVariantNumeric: 'tabular-nums' }}>
      <Icon as={stays ? LuCheck : LuX} boxSize="18px" color={stays ? 'redesign.amount.income' : 'redesign.text.muted'} flexShrink={0} />
      <Text fontSize="sm" color={stays ? 'redesign.heading' : 'redesign.text.muted'} minW="96px">
        {t(stays ? 'checkup.readTwice.readToday' : 'checkup.readTwice.readBefore')}
      </Text>
      <Text
        fontSize="sm"
        fontWeight={stays ? 600 : 400}
        color={stays ? AMOUNT_COLOR[side?.direction] || 'redesign.heading' : 'redesign.text.muted'}
        textDecoration={stays ? undefined : 'line-through'}
        flex={1}
      >
        {directedMoney(side, currency)}
      </Text>
      <Tag tone={stays ? 'green' : 'muted'}>{t(stays ? 'checkup.readTwice.stays' : 'checkup.readTwice.goes')}</Tag>
    </Flex>
  );
}

// One movement of the list: the movement, the reading that stays, the one that goes.
function Row({ item, details, names }) {
  const { t } = useTranslation('repairs');
  const change = item.change || {};
  const currency = change.currency || factsOf(item, change.movement_id)?.currency;
  const { title, meta } = movementLabel(details[change.movement_id], factsOf(item, change.movement_id), names, t);
  return (
    <Box
      display="grid"
      gridTemplateColumns="minmax(0, 1fr) 200px 260px"
      gap={4}
      alignItems="center"
      px={[3, 4, 6]}
      py={3}
      borderBottom="1px solid"
      borderColor="redesign.border"
      sx={{ fontVariantNumeric: 'tabular-nums' }}
    >
      <Box minW={0}>
        <Text fontSize="sm" fontWeight={600} color="redesign.heading" noOfLines={1}>{title}</Text>
        <Text fontSize="xs" color="redesign.text.muted" noOfLines={1}>{meta}</Text>
      </Box>
      <Flex align="center" gap={2}>
        <Icon as={LuCheck} boxSize="16px" color="redesign.amount.income" />
        <Text fontSize="sm" fontWeight={600} color={AMOUNT_COLOR[change.after?.direction] || 'redesign.heading'}>
          {directedMoney(change.after, currency)}
        </Text>
      </Flex>
      <Flex align="center" gap={2} justify="flex-end">
        <Text fontSize="sm" color="redesign.text.muted" textDecoration="line-through">{directedMoney(change.before, currency)}</Text>
        <Tag tone="muted">{t('checkup.readTwice.goes')}</Tag>
      </Flex>
    </Box>
  );
}

/**
 * A statement read twice: an earlier import left a second reading of some
 * bank rows on the movements that already hold today's reading. The review
 * leads with what the person sees (the warnings those readings raise), says
 * that nothing else changes, shows one movement as an example, and asks one
 * decision for every extra reading. The list of all of them is a click away.
 * The movements themselves are never shown as removed: only the extra
 * reading goes. The phone has its own screens (`ReadTwicePhoneReview`).
 */
export default function ReadTwiceReview({ readTwice, sameCount, details, names, onApplyAll, onNotNow, applying }) {
  const { t } = useTranslation('repairs');
  const [open, setOpen] = useState(false);
  const { items, warnings } = readTwice;
  const count = items.length;
  const example = items[0];
  const exampleChange = example?.change || {};
  const exampleCurrency = exampleChange.currency || factsOf(example, exampleChange.movement_id)?.currency;
  const exampleLabel = example
    ? movementLabel(details[exampleChange.movement_id], factsOf(example, exampleChange.movement_id), names, t)
    : null;

  return (
    <VStack align="stretch" spacing={5} pb="120px">
      <Box {...cardSurface} borderRadius="14px" p={[4, 5, 6]}>
        <VStack align="stretch" spacing={5}>
          <Flex gap={4} align="flex-start">
            <Flex w="40px" h="40px" borderRadius="full" bg="phone.alertBg" align="center" justify="center" flexShrink={0}>
              <Icon as={LuTriangleAlert} boxSize="20px" color="phone.alert" />
            </Flex>
            <Box>
              <Text as="h2" fontSize={['md', 'lg']} fontWeight={600} color="redesign.heading" sx={{ textWrap: 'balance' }}>
                {warnings > 0
                  ? t(`checkup.readTwice.warnings${plural(warnings)}`, { count: warnings })
                  : t(`checkup.readTwice.title${plural(count)}`, { count })}
              </Text>
              <Text mt={2} fontSize="sm" color="redesign.text" maxW="700px">{t('checkup.readTwice.story')}</Text>
            </Box>
          </Flex>

          <SimpleGrid columns={[1, 3]} spacing={3}>
            {warnings > 0 && (
              <Tile label={t('checkup.readTwice.tiles.warnings')} value={warnings} good={0} sub={t('checkup.readTwice.tiles.warningsSub')} />
            )}
            <Tile label={t('checkup.readTwice.tiles.balance')} value={t('checkup.readTwice.tiles.noChange')} sub={t('checkup.readTwice.tiles.balanceSub')} />
            <Tile label={t('checkup.readTwice.tiles.movements')} value={t('checkup.readTwice.tiles.noChange')} sub={t('checkup.readTwice.tiles.movementsSub')} />
          </SimpleGrid>

          {example && (
            <VStack align="stretch" spacing={3} p={4} borderRadius="10px" border="1px dashed" borderColor="redesign.border">
              <Text textStyle="microLabel">
                {t('checkup.readTwice.example', { count })}
              </Text>
              <Flex justify="space-between" align="baseline" gap={3} wrap="wrap">
                <Box minW={0}>
                  <Text as="span" fontSize="sm" fontWeight={600} color="redesign.heading">{exampleLabel.title}</Text>
                  <Text as="span" fontSize="xs" color="redesign.text.muted">{` · ${exampleLabel.meta}`}</Text>
                </Box>
                <Text fontSize="sm" fontWeight={600} color={AMOUNT_COLOR[exampleChange.after?.direction] || 'redesign.heading'} sx={{ fontVariantNumeric: 'tabular-nums' }}>
                  {directedMoney(exampleChange.after, exampleCurrency)}
                </Text>
              </Flex>
              <VStack align="stretch" spacing={2} pl={3} borderLeft="2px solid" borderColor="redesign.border">
                <Reading side={exampleChange.after} currency={exampleCurrency} stays />
                <Reading side={exampleChange.before} currency={exampleCurrency} stays={false} />
              </VStack>
            </VStack>
          )}
        </VStack>
      </Box>

      <Box {...cardSurface} borderRadius="14px" overflow="hidden">
        <Button
          variant="ghost"
          w="100%"
          h="auto"
          px={[3, 4, 6]}
          py={4}
          justifyContent="space-between"
          borderRadius={0}
          fontWeight={600}
          color="redesign.heading"
          rightIcon={open ? <LuChevronUp /> : <LuChevronDown />}
          onClick={() => setOpen((value) => !value)}
          aria-expanded={open}
        >
          {open ? t('checkup.readTwice.hide') : t(`checkup.readTwice.seeAll${plural(count)}`, { count })}
        </Button>
        {open && (
          <Box borderTop="1px solid" borderColor="redesign.border">
            <Box
              display="grid"
              gridTemplateColumns="minmax(0, 1fr) 200px 260px"
              gap={4}
              px={[3, 4, 6]}
              py={2}
              bg="redesign.bg.hover"
              textStyle="microLabel"
            >
              <Text fontSize="2xs">{t('checkup.readTwice.columns.movement')}</Text>
              <Text fontSize="2xs">{t('checkup.readTwice.columns.stays')}</Text>
              <Text fontSize="2xs" textAlign="right">{t('checkup.readTwice.columns.goes')}</Text>
            </Box>
            {items.map((item) => <Row key={item.id} item={item} details={details} names={names} />)}
          </Box>
        )}
        {sameCount > 0 && (
          <Flex px={[3, 4, 6]} py={3} gap={3} align="center" bg="redesign.bg.hover" borderTop="1px solid" borderColor="redesign.border">
            <Icon as={LuCheck} boxSize="16px" color="redesign.amount.income" />
            <Text fontSize="sm" color="redesign.text">{t('checkup.changes.same', { count: sameCount })}</Text>
          </Flex>
        )}
      </Box>

      <Flex
        position="sticky"
        bottom={0}
        mx={[-4, -6, -8]}
        px={[4, 6, 8]}
        py={3}
        pb={['calc(12px + env(safe-area-inset-bottom, 0px))', 3]}
        bg="redesign.bg.surface"
        borderTop="1px solid"
        borderColor="redesign.border"
        justify="space-between"
        align={['stretch', 'center']}
        direction={['column', 'row']}
        gap={3}
        zIndex={2}
      >
        <Box>
          <Text fontSize="sm" fontWeight={600} color="redesign.heading">{t(`checkup.readTwice.ready${plural(count)}`, { count })}</Text>
          <Text fontSize="xs" color="redesign.text.muted">{t('checkup.readTwice.readySub')}</Text>
        </Box>
        <Flex gap={2} direction={['column-reverse', 'row']}>
          <Button variant="outline" onClick={onNotNow} isDisabled={applying}>{t('checkup.footer.notNow')}</Button>
          <Button colorScheme="blue" onClick={() => onApplyAll(items)} isLoading={applying}>
            {t(`checkup.readTwice.apply${plural(count)}`)}
          </Button>
        </Flex>
      </Flex>
    </VStack>
  );
}
